/** Country → default timezone/currency plus the options to show for that country. */

export type CountryLocale = {
  label: string;
  /** Default IANA timezone */
  timezone: string;
  /** Default ISO currency */
  currency: string;
  /** Timezones offered for this country (default first) */
  timezones: string[];
  /** Currencies offered for this country (default first) */
  currencies: string[];
};

const CURRENCY_LABELS: Record<string, string> = {
  INR: 'INR — Indian Rupee',
  USD: 'USD — US Dollar',
  EUR: 'EUR — Euro',
  GBP: 'GBP — British Pound',
  AED: 'AED — UAE Dirham',
  SGD: 'SGD — Singapore Dollar',
  AUD: 'AUD — Australian Dollar',
  CAD: 'CAD — Canadian Dollar',
  JPY: 'JPY — Japanese Yen',
  NZD: 'NZD — New Zealand Dollar',
  CHF: 'CHF — Swiss Franc',
  CNY: 'CNY — Chinese Yuan',
  HKD: 'HKD — Hong Kong Dollar',
  MYR: 'MYR — Malaysian Ringgit',
  THB: 'THB — Thai Baht',
  IDR: 'IDR — Indonesian Rupiah',
  PHP: 'PHP — Philippine Peso',
  VND: 'VND — Vietnamese Dong',
  KRW: 'KRW — South Korean Won',
  SAR: 'SAR — Saudi Riyal',
  QAR: 'QAR — Qatari Riyal',
  KWD: 'KWD — Kuwaiti Dinar',
  BHD: 'BHD — Bahraini Dinar',
  OMR: 'OMR — Omani Rial',
  EGP: 'EGP — Egyptian Pound',
  ZAR: 'ZAR — South African Rand',
  NGN: 'NGN — Nigerian Naira',
  KES: 'KES — Kenyan Shilling',
  BRL: 'BRL — Brazilian Real',
  MXN: 'MXN — Mexican Peso',
  ARS: 'ARS — Argentine Peso',
  CLP: 'CLP — Chilean Peso',
  COP: 'COP — Colombian Peso',
  PEN: 'PEN — Peruvian Sol',
  TRY: 'TRY — Turkish Lira',
  RUB: 'RUB — Russian Ruble',
  PLN: 'PLN — Polish Zloty',
  SEK: 'SEK — Swedish Krona',
  NOK: 'NOK — Norwegian Krone',
  DKK: 'DKK — Danish Krone',
  CZK: 'CZK — Czech Koruna',
  HUF: 'HUF — Hungarian Forint',
  RON: 'RON — Romanian Leu',
  ILS: 'ILS — Israeli Shekel',
  PKR: 'PKR — Pakistani Rupee',
  BDT: 'BDT — Bangladeshi Taka',
  LKR: 'LKR — Sri Lankan Rupee',
  NPR: 'NPR — Nepalese Rupee',
};

const TIMEZONE_LABELS: Record<string, string> = {
  UTC: 'UTC',
  'Asia/Kolkata': 'Asia/Kolkata (IST)',
  'Asia/Dubai': 'Asia/Dubai (GST)',
  'Asia/Singapore': 'Asia/Singapore (SGT)',
  'Asia/Tokyo': 'Asia/Tokyo (JST)',
  'Asia/Shanghai': 'Asia/Shanghai (CST)',
  'Asia/Hong_Kong': 'Asia/Hong_Kong (HKT)',
  'Asia/Kuala_Lumpur': 'Asia/Kuala_Lumpur (MYT)',
  'Asia/Bangkok': 'Asia/Bangkok (ICT)',
  'Asia/Jakarta': 'Asia/Jakarta (WIB)',
  'Asia/Makassar': 'Asia/Makassar (WITA)',
  'Asia/Jayapura': 'Asia/Jayapura (WIT)',
  'Asia/Manila': 'Asia/Manila (PHT)',
  'Asia/Ho_Chi_Minh': 'Asia/Ho_Chi_Minh (ICT)',
  'Asia/Seoul': 'Asia/Seoul (KST)',
  'Asia/Riyadh': 'Asia/Riyadh (AST)',
  'Asia/Qatar': 'Asia/Qatar (AST)',
  'Asia/Kuwait': 'Asia/Kuwait (AST)',
  'Asia/Bahrain': 'Asia/Bahrain (AST)',
  'Asia/Muscat': 'Asia/Muscat (GST)',
  'Asia/Karachi': 'Asia/Karachi (PKT)',
  'Asia/Dhaka': 'Asia/Dhaka (BST)',
  'Asia/Colombo': 'Asia/Colombo (IST)',
  'Asia/Kathmandu': 'Asia/Kathmandu (NPT)',
  'Asia/Jerusalem': 'Asia/Jerusalem (IST)',
  'Europe/London': 'Europe/London (GMT/BST)',
  'Europe/Berlin': 'Europe/Berlin (CET)',
  'Europe/Paris': 'Europe/Paris (CET)',
  'Europe/Amsterdam': 'Europe/Amsterdam (CET)',
  'Europe/Brussels': 'Europe/Brussels (CET)',
  'Europe/Vienna': 'Europe/Vienna (CET)',
  'Europe/Helsinki': 'Europe/Helsinki (EET)',
  'Europe/Rome': 'Europe/Rome (CET)',
  'Europe/Madrid': 'Europe/Madrid (CET)',
  'Europe/Lisbon': 'Europe/Lisbon (WET)',
  'Europe/Dublin': 'Europe/Dublin (IST)',
  'Europe/Zurich': 'Europe/Zurich (CET)',
  'Europe/Stockholm': 'Europe/Stockholm (CET)',
  'Europe/Oslo': 'Europe/Oslo (CET)',
  'Europe/Copenhagen': 'Europe/Copenhagen (CET)',
  'Europe/Warsaw': 'Europe/Warsaw (CET)',
  'Europe/Prague': 'Europe/Prague (CET)',
  'Europe/Budapest': 'Europe/Budapest (CET)',
  'Europe/Bucharest': 'Europe/Bucharest (EET)',
  'Europe/Athens': 'Europe/Athens (EET)',
  'Europe/Istanbul': 'Europe/Istanbul (TRT)',
  'Europe/Moscow': 'Europe/Moscow (MSK)',
  'America/New_York': 'America/New_York (ET)',
  'America/Chicago': 'America/Chicago (CT)',
  'America/Denver': 'America/Denver (MT)',
  'America/Los_Angeles': 'America/Los_Angeles (PT)',
  'America/Phoenix': 'America/Phoenix (MST)',
  'America/Anchorage': 'America/Anchorage (AKT)',
  'Pacific/Honolulu': 'Pacific/Honolulu (HST)',
  'America/Toronto': 'America/Toronto (ET)',
  'America/Vancouver': 'America/Vancouver (PT)',
  'America/Edmonton': 'America/Edmonton (MT)',
  'America/Winnipeg': 'America/Winnipeg (CT)',
  'America/Halifax': 'America/Halifax (AT)',
  'America/Sao_Paulo': 'America/Sao_Paulo (BRT)',
  'America/Mexico_City': 'America/Mexico_City (CST)',
  'America/Buenos_Aires': 'America/Buenos_Aires (ART)',
  'America/Santiago': 'America/Santiago (CLT)',
  'America/Bogota': 'America/Bogota (COT)',
  'America/Lima': 'America/Lima (PET)',
  'Australia/Sydney': 'Australia/Sydney (AEST)',
  'Australia/Melbourne': 'Australia/Melbourne (AEST)',
  'Australia/Brisbane': 'Australia/Brisbane (AEST)',
  'Australia/Perth': 'Australia/Perth (AWST)',
  'Australia/Adelaide': 'Australia/Adelaide (ACST)',
  'Australia/Darwin': 'Australia/Darwin (ACST)',
  'Pacific/Auckland': 'Pacific/Auckland (NZST)',
  'Africa/Cairo': 'Africa/Cairo (EET)',
  'Africa/Johannesburg': 'Africa/Johannesburg (SAST)',
  'Africa/Lagos': 'Africa/Lagos (WAT)',
  'Africa/Nairobi': 'Africa/Nairobi (EAT)',
};

function locale(
  label: string,
  timezone: string,
  currency: string,
  timezones?: string[],
  currencies?: string[],
): CountryLocale {
  const tzs = timezones?.length ? timezones : [timezone];
  const curs = currencies?.length ? currencies : [currency];
  return {
    label,
    timezone: tzs[0] || timezone,
    currency: curs[0] || currency,
    timezones: tzs[0] === timezone ? tzs : [timezone, ...tzs.filter((t) => t !== timezone)],
    currencies: curs[0] === currency ? curs : [currency, ...curs.filter((c) => c !== currency)],
  };
}

/** ISO country code → locale defaults + selectable timezone/currency lists. */
export const COUNTRY_DEFAULTS: Record<string, CountryLocale> = {
  IN: locale('India', 'Asia/Kolkata', 'INR'),
  US: locale('United States', 'America/New_York', 'USD', [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  ]),
  GB: locale('United Kingdom', 'Europe/London', 'GBP'),
  AE: locale('United Arab Emirates', 'Asia/Dubai', 'AED'),
  SG: locale('Singapore', 'Asia/Singapore', 'SGD'),
  AU: locale('Australia', 'Australia/Sydney', 'AUD', [
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
    'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin',
  ]),
  CA: locale('Canada', 'America/Toronto', 'CAD', [
    'America/Toronto', 'America/Vancouver', 'America/Edmonton',
    'America/Winnipeg', 'America/Halifax',
  ], ['CAD', 'USD']),
  DE: locale('Germany', 'Europe/Berlin', 'EUR'),
  FR: locale('France', 'Europe/Paris', 'EUR'),
  ES: locale('Spain', 'Europe/Madrid', 'EUR'),
  IT: locale('Italy', 'Europe/Rome', 'EUR'),
  NL: locale('Netherlands', 'Europe/Amsterdam', 'EUR'),
  BE: locale('Belgium', 'Europe/Brussels', 'EUR', ['Europe/Brussels', 'Europe/Paris'], ['EUR']),
  PT: locale('Portugal', 'Europe/Lisbon', 'EUR'),
  IE: locale('Ireland', 'Europe/Dublin', 'EUR'),
  CH: locale('Switzerland', 'Europe/Zurich', 'CHF', undefined, ['CHF', 'EUR']),
  AT: locale('Austria', 'Europe/Vienna', 'EUR', ['Europe/Vienna', 'Europe/Berlin']),
  SE: locale('Sweden', 'Europe/Stockholm', 'SEK', undefined, ['SEK', 'EUR']),
  NO: locale('Norway', 'Europe/Oslo', 'NOK'),
  DK: locale('Denmark', 'Europe/Copenhagen', 'DKK', undefined, ['DKK', 'EUR']),
  FI: locale('Finland', 'Europe/Helsinki', 'EUR', ['Europe/Helsinki']),
  PL: locale('Poland', 'Europe/Warsaw', 'PLN', undefined, ['PLN', 'EUR']),
  CZ: locale('Czech Republic', 'Europe/Prague', 'CZK', undefined, ['CZK', 'EUR']),
  HU: locale('Hungary', 'Europe/Budapest', 'HUF', undefined, ['HUF', 'EUR']),
  RO: locale('Romania', 'Europe/Bucharest', 'RON', undefined, ['RON', 'EUR']),
  GR: locale('Greece', 'Europe/Athens', 'EUR'),
  TR: locale('Turkey', 'Europe/Istanbul', 'TRY'),
  RU: locale('Russia', 'Europe/Moscow', 'RUB'),
  JP: locale('Japan', 'Asia/Tokyo', 'JPY'),
  CN: locale('China', 'Asia/Shanghai', 'CNY'),
  HK: locale('Hong Kong', 'Asia/Hong_Kong', 'HKD', undefined, ['HKD', 'CNY', 'USD']),
  MY: locale('Malaysia', 'Asia/Kuala_Lumpur', 'MYR'),
  TH: locale('Thailand', 'Asia/Bangkok', 'THB'),
  ID: locale('Indonesia', 'Asia/Jakarta', 'IDR', [
    'Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura',
  ]),
  PH: locale('Philippines', 'Asia/Manila', 'PHP'),
  VN: locale('Vietnam', 'Asia/Ho_Chi_Minh', 'VND'),
  KR: locale('South Korea', 'Asia/Seoul', 'KRW'),
  NZ: locale('New Zealand', 'Pacific/Auckland', 'NZD'),
  SA: locale('Saudi Arabia', 'Asia/Riyadh', 'SAR'),
  QA: locale('Qatar', 'Asia/Qatar', 'QAR'),
  KW: locale('Kuwait', 'Asia/Kuwait', 'KWD'),
  BH: locale('Bahrain', 'Asia/Bahrain', 'BHD'),
  OM: locale('Oman', 'Asia/Muscat', 'OMR'),
  EG: locale('Egypt', 'Africa/Cairo', 'EGP'),
  ZA: locale('South Africa', 'Africa/Johannesburg', 'ZAR'),
  NG: locale('Nigeria', 'Africa/Lagos', 'NGN'),
  KE: locale('Kenya', 'Africa/Nairobi', 'KES'),
  BR: locale('Brazil', 'America/Sao_Paulo', 'BRL'),
  MX: locale('Mexico', 'America/Mexico_City', 'MXN'),
  AR: locale('Argentina', 'America/Buenos_Aires', 'ARS'),
  CL: locale('Chile', 'America/Santiago', 'CLP'),
  CO: locale('Colombia', 'America/Bogota', 'COP'),
  PE: locale('Peru', 'America/Lima', 'PEN'),
  IL: locale('Israel', 'Asia/Jerusalem', 'ILS'),
  PK: locale('Pakistan', 'Asia/Karachi', 'PKR'),
  BD: locale('Bangladesh', 'Asia/Dhaka', 'BDT'),
  LK: locale('Sri Lanka', 'Asia/Colombo', 'LKR'),
  NP: locale('Nepal', 'Asia/Kathmandu', 'NPR'),
};

const FALLBACK: CountryLocale = locale('Other', 'UTC', 'USD', ['UTC'], ['USD', 'EUR', 'INR']);

const dynamicCountryMap: Record<string, CountryLocale> = {};

export type CountryOptionLocale = {
  code: string;
  name?: string;
  currency?: string;
  timezone?: string;
  timezones?: string[];
  currencies?: string[];
};

/** Register country locale data loaded from the geo master API (all ISO countries). */
export function registerCountryOptions(options: CountryOptionLocale[]) {
  for (const o of options) {
    const code = (o.code || '').trim().toUpperCase();
    if (!code) continue;
    const hardcoded = COUNTRY_DEFAULTS[code];
    const timezone = o.timezone || hardcoded?.timezone || 'UTC';
    const currency = (o.currency || hardcoded?.currency || 'USD').toUpperCase();
    const timezones = [...new Set([
      timezone,
      ...(o.timezones || []),
      ...(hardcoded?.timezones || []),
    ])];
    const currencies = [...new Set([
      currency,
      ...(o.currencies || []),
      ...(hardcoded?.currencies || []),
    ])];
    dynamicCountryMap[code] = locale(
      o.name || hardcoded?.label || code,
      timezone,
      currency,
      timezones,
      currencies,
    );
  }
}

export function getCountryLocale(countryCode?: string | null): CountryLocale {
  const code = (countryCode || '').trim().toUpperCase();
  if (code && dynamicCountryMap[code]) return dynamicCountryMap[code];
  if (code && COUNTRY_DEFAULTS[code]) return COUNTRY_DEFAULTS[code];
  return { ...FALLBACK, label: code || FALLBACK.label };
}

/** Resolve defaults when a country is picked — prefers API option metadata when present. */
export function defaultsForCountrySelection(countryCode: string, option?: Omit<CountryOptionLocale, 'code'>) {
  const d = getCountryLocale(countryCode);
  const timezone = option?.timezone || d.timezone;
  const currency = (option?.currency || d.currency).toUpperCase();
  return { timezone, currency, label: option?.name || d.label };
}

/** @deprecated use getCountryLocale — kept for existing imports */
export function defaultsForCountry(countryCode: string) {
  const d = getCountryLocale(countryCode);
  return { timezone: d.timezone, currency: d.currency, label: d.label };
}

export function currencyLabel(code: string) {
  return CURRENCY_LABELS[code] || `${code}`;
}

export function timezoneLabel(iana: string) {
  return TIMEZONE_LABELS[iana] || iana;
}

export type SelectOption = { value: string; label: string };

/** Currency dropdown options for the selected country (includes current value if set). */
export function currencyOptionsForCountry(countryCode?: string | null, current?: string | null): SelectOption[] {
  const d = getCountryLocale(countryCode);
  const codes = [...d.currencies];
  const cur = (current || '').trim().toUpperCase();
  if (cur && !codes.includes(cur)) codes.unshift(cur);
  return codes.map((c) => ({ value: c, label: currencyLabel(c) }));
}

/** Timezone dropdown options for the selected country (includes current value if set). */
export function timezoneOptionsForCountry(countryCode?: string | null, current?: string | null): SelectOption[] {
  const d = getCountryLocale(countryCode);
  const zones = [...d.timezones];
  const cur = (current || '').trim();
  if (cur && !zones.includes(cur)) zones.unshift(cur);
  return zones.map((z) => ({ value: z, label: timezoneLabel(z) }));
}

/** Flat lists used where a global picker is still needed (subscription plans, etc.). */
export const TIMEZONE_OPTIONS: SelectOption[] = (() => {
  const seen = new Set<string>();
  const out: SelectOption[] = [];
  for (const d of Object.values(COUNTRY_DEFAULTS)) {
    for (const z of d.timezones) {
      if (seen.has(z)) continue;
      seen.add(z);
      out.push({ value: z, label: timezoneLabel(z) });
    }
  }
  if (!seen.has('UTC')) out.unshift({ value: 'UTC', label: timezoneLabel('UTC') });
  return out.sort((a, b) => a.label.localeCompare(b.label));
})();

export const CURRENCY_OPTIONS: SelectOption[] = (() => {
  const seen = new Set<string>();
  const out: SelectOption[] = [];
  for (const d of Object.values(COUNTRY_DEFAULTS)) {
    for (const c of d.currencies) {
      if (seen.has(c)) continue;
      seen.add(c);
      out.push({ value: c, label: currencyLabel(c) });
    }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
})();

export const COUNTRY_OPTIONS = Object.entries(COUNTRY_DEFAULTS).map(([code, d]) => ({
  value: code,
  label: `${d.label} (${code})`,
}));
